import {FC, useEffect} from 'react'

type DuplicateAppointmentItem = {
  code: number
  requesterName: string
  date: string
  timeRange: string
  languageTo: string
  status: string
}

type DuplicateAppointmentModalProps = {
  duplicates: DuplicateAppointmentItem[]
  onProceed: () => void
  onCancel: () => void
}

const OLIVE = '#6B7C2E'

const styles: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    zIndex: 1050,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    width: '100%',
    maxWidth: '520px',
    boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '24px 24px 20px 24px',
    gap: '12px',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
  },
  iconWrap: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: '#FEE2E2',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: '1px',
  },
  title: {
    margin: 0,
    fontSize: '15px',
    fontWeight: 700,
    color: '#111827',
    lineHeight: '22px',
  },
  subtitle: {
    margin: '3px 0 0 0',
    fontSize: '13px',
    color: '#6B7280',
    lineHeight: '1.55',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    padding: '2px',
    cursor: 'pointer',
    color: '#9CA3AF',
    fontSize: '18px',
    lineHeight: 1,
    flexShrink: 0,
  },
  body: {
    padding: '0 24px 24px 24px',
  },
  sectionLabel: {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.07em',
    color: '#9CA3AF',
    textTransform: 'uppercase' as const,
    marginBottom: '12px',
  },
  cardList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '10px',
  },
  card: {
    border: '1px solid #E5E7EB',
    borderRadius: '8px',
    backgroundColor: '#F9FAFB',
    padding: '14px 16px',
  },
  aptLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '14px',
    fontWeight: 700,
    color: OLIVE,
    textDecoration: 'none',
    lineHeight: '20px',
    marginBottom: '10px',
  },
  detailRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
    flexWrap: 'wrap' as const,
    marginBottom: '8px',
  },
  detailItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '13px',
    color: '#374151',
    lineHeight: '18px',
  },
  timeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '13px',
    color: '#374151',
    lineHeight: '18px',
  },
  icon: {
    fontSize: '13px',
    color: '#9CA3AF',
    display: 'flex',
    alignItems: 'center',
    lineHeight: 1,
    flexShrink: 0,
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    padding: '16px 24px 24px 24px',
    borderTop: '1px solid #F3F4F6',
  },
  cancelBtn: {
    flex: 1,
    maxWidth: '180px',
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    backgroundColor: '#fff',
    border: '1px solid #D1D5DB',
    borderRadius: '8px',
    cursor: 'pointer',
    lineHeight: 1,
  },
  proceedBtn: {
    flex: 1,
    maxWidth: '180px',
    height: '42px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: OLIVE,
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    lineHeight: 1,
  },
}

const DuplicateAppointmentModal: FC<DuplicateAppointmentModalProps> = ({
  duplicates,
  onProceed,
  onCancel,
}) => {
  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  return (
    <div style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <div style={styles.modal}>

        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <div style={styles.iconWrap}>
              <i className='bi bi-exclamation-triangle' style={{color: '#EF4444', fontSize: '16px'}} />
            </div>
            <div>
              <p style={styles.title}>Possible Duplicate Detected</p>
              <p style={styles.subtitle}>
                We found existing appointments around the same time.<br />
                Please review before proceeding to avoid double-booking.
              </p>
            </div>
          </div>
          <button style={styles.closeBtn} onClick={onCancel} type='button' aria-label='Close'>
            <i className='bi bi-x' />
          </button>
        </div>

        <div style={styles.body}>
          <p style={styles.sectionLabel}>
            Existing Appointments ({duplicates.length})
          </p>

          <div style={styles.cardList}>
            {duplicates.map((item) => (
              <div key={item.code} style={styles.card}>

                <a
                  href={`/appointmentViewer/${item.code}`}
                  target='_blank'
                  rel='noreferrer'
                  style={styles.aptLink}
                >
                  APT-{item.code}
                  <i className='bi bi-box-arrow-up-right' style={{fontSize: '12px', lineHeight: 1}} />
                </a>

                <div style={styles.detailRow}>
                  <span style={styles.detailItem}>
                    <span style={styles.icon}><i className='bi bi-person' /></span>
                    {item.requesterName || '—'}
                  </span>
                  <span style={styles.detailItem}>
                    <span style={styles.icon}><i className='bi bi-calendar3' /></span>
                    {item.date || '—'}
                  </span>
                  <span style={styles.detailItem}>
                    <span style={styles.icon}><i className='bi bi-translate' /></span>
                    {item.languageTo || '—'}
                  </span>
                </div>

                <div style={styles.timeRow}>
                  <span style={styles.icon}><i className='bi bi-clock' /></span>
                  {item.timeRange || '—'}
                </div>

              </div>
            ))}
          </div>
        </div>

        <div style={styles.footer}>
          <button style={styles.cancelBtn} type='button' onClick={onCancel}>
            Cancel
          </button>
          <button style={styles.proceedBtn} type='button' onClick={onProceed}>
            Proceed Anyway
          </button>
        </div>

      </div>
    </div>
  )
}

export {DuplicateAppointmentModal}
